/**
 * MonthlyProposalActivityWidget
 * SPEC-monthly-proposal-activity (issue #151)
 *
 * Renders INSIDE a titled CollapsibleSection on the dashboard, so it deliberately
 * does NOT draw its own card frame or title — that would duplicate the section
 * header (and double-border the panel). It renders content directly, matching the
 * RevenueGoalWidget / UpcomingTasks convention for a sole-occupant section.
 *
 * Shows the CURRENT month at a glance:
 *   "{Month YYYY} — {n} prepared ({$}) / {m} won ({$})"   (prepared blue, won green)
 * and expands to a 12-month GROUPED bar chart comparing prepared vs won.
 *
 * Conventions mirrored:
 *   - recharts BarChart with abbreviated $ Y-axis (see components/reports/RevenueChart.tsx)
 *   - GROUPED (not stacked): the two <Bar> series carry NO shared stackId, so recharts
 *     renders them side-by-side. Won is not a within-month component of Prepared (a
 *     proposal prepared in March may be won in May), so stacking would mislead (Q6).
 */
import { useState } from 'react';
import { useProposalActivity, ProposalActivityMonth } from '@/hooks/useDashboardReports';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Full currency — used in the hover tooltip where precision is wanted.
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Abbreviated currency for glanceable surfaces (headline + Y-axis): $0, $45k,
// $320k, $1.1M. Keeps the headline on one line and the axis readable above $1M
// (a plain $k formatter would render a $1.09M month as an ugly "$1090k").
function formatCompactCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${value}`;
}

export function MonthlyProposalActivityWidget() {
  const { data, isLoading, error } = useProposalActivity(12);
  const [isExpanded, setIsExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse" data-testid="monthly-proposal-activity-widget">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="monthly-proposal-activity-widget"
      >
        Failed to load proposal activity
      </p>
    );
  }

  const months: ProposalActivityMonth[] = data.months ?? [];
  // Current month = LAST element of the ascending (oldest-first) series.
  const current = months.length > 0 ? months[months.length - 1] : null;

  // Empty = no months at all, OR every month carries zero activity.
  const hasActivity = months.some(
    (m) => m.prepared_count > 0 || m.won_count > 0 || m.prepared_value > 0 || m.won_value > 0,
  );

  return (
    <div className="space-y-4" data-testid="monthly-proposal-activity-widget">
      {/* Current-month headline. Prepared/Won are colored to match the chart bars
          (blue / green) so the glance value ties straight to the trend below. */}
      {current ? (
        <p className="text-base font-semibold" data-testid="proposal-activity-headline">
          {current.month_label} —{' '}
          <span className="text-blue-600">
            {current.prepared_count} prepared ({formatCompactCurrency(current.prepared_value)})
          </span>{' '}
          /{' '}
          <span className="text-emerald-600">
            {current.won_count} won ({formatCompactCurrency(current.won_value)})
          </span>
        </p>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="proposal-activity-empty">
          No proposal activity yet
        </p>
      )}

      {/* Expand toggle — reveals the 12-month grouped bar chart. Only shown when
          there is actually a trend to reveal (hidden on an all-zero / empty widget). */}
      {hasActivity && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          aria-expanded={isExpanded}
          data-testid="proposal-activity-toggle"
        >
          <ChevronDown
            className={cn('h-4 w-4 transition-transform duration-200', !isExpanded && '-rotate-90')}
            aria-hidden="true"
          />
          {isExpanded ? 'Hide 12-month trend' : 'Show 12-month trend'}
        </button>
      )}

      {isExpanded && (
        <div data-testid="proposal-activity-chart">
          {/* The toggle above only renders when hasActivity, so reaching here
              guarantees there is real data to chart. */}
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={months} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="0" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="month_label"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                angle={-45}
                textAnchor="end"
                height={80}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tickFormatter={(value) => formatCompactCurrency(value)}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
              />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="square" />
              {/* GROUPED bars — deliberately NO stackId on either series. */}
              <Bar dataKey="prepared_value" name="Prepared" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="won_value" name="Won" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
